import { useMemo } from 'react';
import { useBIMStore } from '../state/useBIMStore';
import { AnnotationScaleManager } from '../core/graphics/AnnotationScaleManager';
import type { View } from '../core/types';

// Stateless helper: instantiate locally instead of pulling the full
// core/services singleton graph into the hot render path.
const scaleManager = new AnnotationScaleManager();

/**
 * BIM rule: the model is constant; every view owns its scale, and
 * annotations keep a fixed PAPER size — their world size is
 * paper * viewScale. These hooks expose the effective scale of the active
 * view to renderers so text, tags, dimensions and symbols resize with it.
 */

/**
 * Effective scale denominator used for annotation sizing.
 * 3D / Legend views are presentation geometry, not paper output: their
 * default scale of 1 would shrink annotations to nothing, so they keep
 * the 1:100 authoring baseline. Plan / section / elevation views map
 * paper size to world size exactly: world = paper * viewScale (BIM rule).
 */
export function effectiveAnnotationScale(view?: View | null): number {
    const scale = scaleManager.resolveViewScale(view);
    if (view && (view.type === '3D' || view.type === 'Legend')) return 100;
    return scale;
}

/**
 * Raw view scale denominator (100 for 1:100, safe default 100). Used for the
 * orthographic camera paper-space sync: view zoom is inversely proportional
 * to the scale, so annotations (world = paper * scale) keep a constant
 * on-screen size while the model doubles when going 1:100 -> 1:50.
 */
export function rawViewScale(view?: View | null): number {
    return scaleManager.resolveViewScale(view);
}

/**
 * Active view annotation scale (BIM rule: every view owns its scale
 * independently; the model stays constant, only the paper-to-world mapping
 * of annotations changes).
 */
export function useActiveViewScale(): number {
    return useBIMStore((s) => effectiveAnnotationScale(s.project.views.get(s.project.activeViewId)));
}

/**
 * Annotation scaling factor relative to the 1:100 design baseline.
 * Hard-coded annotation sizes in the renderers are authored at 1:100, so
 * world size = baseline size * (viewScale / 100). A 2.5 mm paper text stays
 * constant on paper at any scale: at 1:50 it renders half as large in the
 * model, at 1:200 twice as large — exactly like BIM.
 */
export function useAnnotationScaleFactor(): number {
    const viewScale = useActiveViewScale();
    return useMemo(() => viewScale / 100, [viewScale]);
}
